import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  ClientJobStatus,
  JobStatus,
  SheetData,
  statusToLabel,
} from "../../../pages/playground/types";
import { useAuthStore } from "../../../authProvider";

type JobListFiltersProps = {
  statusFilter: JobStatus | null;
  setStatusFilter: (value: JobStatus | null) => void;
  columnFilter: string | null;
  setColumnFilter: (value: string | null) => void;
  totalJobs: number;
  jobsByStatus?: Record<JobStatus, number>;
  sheetData: SheetData;
  isAIEnabled?: boolean;
};

const JobListFilters: React.FC<JobListFiltersProps> = ({
  statusFilter,
  setStatusFilter,
  columnFilter,
  setColumnFilter,
  totalJobs,
  jobsByStatus,
  sheetData,
  isAIEnabled = false,
}) => {
  const { user } = useAuthStore();

  const SPECIAL_COLUMNS = {
    GARMENT_TYPE: "garment_type",
  };

  // Derive available columns from sheetData
  const availableColumns = useMemo(() => {
    if (!sheetData?.items?.length) return [];

    const columns = Object.keys(sheetData.items[0]).filter(
      (key) =>
        // Filter out any functions or non-data properties
        typeof sheetData.items[0][key] !== "function" && key !== "__proto__"
    );

    // Filter out garment_type from columns as we'll add it separately
    const filteredColumns = columns.filter(
      (col) => col !== SPECIAL_COLUMNS.GARMENT_TYPE
    );

    // Add special columns from job properties
    return filteredColumns;
  }, [sheetData, SPECIAL_COLUMNS.GARMENT_TYPE]);

  // Extract first item values for preview
  const firstItemValues = useMemo(() => {
    if (!sheetData?.items?.length) return {};

    const firstItem = sheetData.items[0];
    const values: Record<string, string> = {};

    availableColumns.forEach((column) => {
      if (firstItem[column] !== undefined) {
        values[column] = String(firstItem[column]);
      }
    });

    return values;
  }, [sheetData, availableColumns]);

  // Handler for status filter changes
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    // Convert string value to JobStatus or null
    const value = event.target.value;
    setStatusFilter(value === "all" ? null : (value as JobStatus));
  };

  // Handler for column filter changes
  const handleColumnChange = (event: SelectChangeEvent<string>) => {
    setColumnFilter(event.target.value === "" ? null : event.target.value);
  };

  const availableStatuses = useMemo(() => {
    if (user?.isClientRelated) {
      return Object.values(ClientJobStatus);
    } else {
      return Object.values(JobStatus);
    }
  }, [user]);

  return (
    <Box
      sx={{
        px: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          width: "100%",
        }}
      >
        {/* Status filter */}
        <Box sx={{ width: "100%" }}>
          <FormControl size="small" sx={{ width: "100%" }}>
            <InputLabel id="status-filter-label">Status filter</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status filter"
              value={statusFilter === null ? "all" : statusFilter}
              onChange={handleStatusChange}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 300 },
                },
              }}
            >
              <MenuItem value="all" sx={{ fontWeight: 600 }}>
                All Statuses ({totalJobs})
              </MenuItem>
              {availableStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography>
                      {statusToLabel(status)}

                      {jobsByStatus && jobsByStatus[status] ? (
                        <> ({jobsByStatus?.[status]})</>
                      ) : null}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Column filter */}
        <Box sx={{ width: "100%" }}>
          <FormControl size="small" sx={{ width: "100%" }}>
            <InputLabel id="column-filter-label">Group by column</InputLabel>
            <Select
              labelId="column-filter-label"
              label="Group by column"
              value={columnFilter || ""}
              onChange={handleColumnChange}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 300 },
                },
              }}
            >
              <MenuItem value="" sx={{ fontWeight: 600 }}>
                <em>No grouping</em>
              </MenuItem>
              {isAIEnabled && (
                <MenuItem value="garment_type">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography>Garment Type</Typography>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      AI Generated
                    </Typography>
                  </Box>
                </MenuItem>
              )}
              {availableColumns.map((column) => (
                <MenuItem key={column} value={column}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography>{column}</Typography>
                    {firstItemValues[column] && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          ml: 1,
                          maxWidth: "40%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {String(firstItemValues[column]).substring(0, 15)}
                        {String(firstItemValues[column]).length > 15
                          ? "..."
                          : ""}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default JobListFilters;
